import React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { listMyOrders, getReceiptUrl, rebuyCheckout } from '../api';
import { isSafeUrl, goTo } from './urls';
import { formatDate, formatInstant } from './dates';

const money = (cents, currency = 'usd') => (cents == null ? '-' : new Intl.NumberFormat(undefined, {
  style: 'currency', currency: (currency || 'usd').toUpperCase(),
}).format(cents / 100));

const DAY_FMT = { year: 'numeric', month: 'short', day: 'numeric' };
// purchased_at is a timestamp (localize it); valid_until is a calendar date
// (must not shift). Same look, different correctness.
const instant = (value) => formatInstant(value, DAY_FMT) || '-';
const calendarDay = (value) => formatDate(value, DAY_FMT) || '-';

function statusLabel(o) {
  if (o.status === 'refunded') return { text: 'Refunded', className: 'text-orange-600 dark:text-orange-400' };
  if (o.status === 'canceled') return { text: 'Canceled', className: 'text-gray-500' };
  if (o.expired) return { text: 'Expired', className: 'text-orange-600 dark:text-orange-400' };
  return { text: 'Paid', className: 'text-blue-600 dark:text-blue-400' };
}

function ReceiptLink({ order }) {
  const fetchReceipt = useMutation({
    mutationFn: () => getReceiptUrl(order.purchase_id),
    onSuccess: (res) => {
      if (!res?.receipt_url) { toast.error('No receipt is available for this order yet'); return; }
      if (!isSafeUrl(res.receipt_url)) { toast.error('That receipt link looks invalid'); return; }
      // No success check on the return value: window.open resolves to null whenever
      // noopener is requested, so testing it reported a popup block on every
      // successful open. A named window still keeps repeat clicks from stacking tabs.
      window.open(res.receipt_url, 'jpro-receipt', 'noopener,noreferrer');
    },
    onError: (e) => toast.error(e?.message || 'Could not load the receipt'),
  });

  if (!order.has_receipt) return <span className="text-gray-400">-</span>;
  return (
    <button
      onClick={() => fetchReceipt.mutate()}
      disabled={fetchReceipt.isPending}
      className="text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40"
    >
      {fetchReceipt.isPending ? 'Opening...' : 'Receipt'}
    </button>
  );
}

function RebuyLink({ order }) {
  const buy = useMutation({
    mutationFn: () => rebuyCheckout(order.rebuy, order.seats || 1),
    onSuccess: (res) => {
      if (!res?.checkout_url) return;
      if (!goTo(res.checkout_url)) toast.error('That checkout link looks invalid');
    },
    onError: (e) => toast.error(e?.message || 'Could not start checkout'),
  });
  if (!order.rebuy) return null;
  return (
    <button
      onClick={() => buy.mutate()}
      disabled={buy.isPending || buy.isSuccess}
      className="text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40"
    >
      {buy.isPending || buy.isSuccess ? 'Redirecting...' : 'Buy again'}
    </button>
  );
}

export default function Orders() {
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['my-training-orders'], queryFn: listMyOrders });

  if (isLoading) return <div className="text-sm text-gray-500">Loading...</div>;
  if (isError) return <div className="text-sm text-red-500">Could not load your orders{error?.message ? `: ${error.message}` : ''}.</div>;

  const orders = data?.orders || [];
  if (orders.length === 0) {
    return <div className="text-sm text-gray-500">You have not placed any training orders yet.</div>;
  }

  // Summed per currency: adding cents across currencies and labelling the result
  // in one of them would be a plainly wrong number.
  const totals = orders
    .filter((o) => o.status !== 'refunded' && o.status !== 'canceled')
    .reduce((acc, o) => {
      const cur = (o.currency || 'usd').toUpperCase();
      acc[cur] = (acc[cur] || 0) + (o.total_cents || 0);
      return acc;
    }, {});
  const totalPairs = Object.entries(totals);

  return (
    <div>
      <p className="text-xs text-gray-500 mb-3">
        Every training order on your account, newest first. Receipts come straight from Stripe.
      </p>
      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200 dark:border-gray-700">
              <th scope="col" className="px-3 py-2 font-semibold">Date</th>
              <th scope="col" className="px-3 py-2 font-semibold">Class</th>
              <th scope="col" className="px-3 py-2 font-semibold text-right">Seats</th>
              <th scope="col" className="px-3 py-2 font-semibold text-right">Amount</th>
              <th scope="col" className="px-3 py-2 font-semibold">Status</th>
              <th scope="col" className="px-3 py-2 font-semibold">Receipt</th>
              <th scope="col" className="px-3 py-2 font-semibold" />
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => {
              const s = statusLabel(o);
              return (
                <tr key={o.purchase_id} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                  <td className="px-3 py-2 whitespace-nowrap text-gray-500">{instant(o.purchased_at)}</td>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{o.class_title || 'Training class'}</div>
                    <div className="text-[11px] text-gray-400">
                      {o.billed_to_org ? 'Billed to your organization' : `Billed to ${o.buyer || 'you'}`}
                      {o.valid_until ? ` · access through ${calendarDay(o.valid_until)}` : ''}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{o.seats}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(o.total_cents, o.currency)}</td>
                  <td className={`px-3 py-2 whitespace-nowrap font-semibold ${s.className}`}>{s.text}</td>
                  <td className="px-3 py-2"><ReceiptLink order={o} /></td>
                  <td className="px-3 py-2 text-right"><RebuyLink order={o} /></td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            {totalPairs.map(([cur, sum]) => (
              <tr key={cur} className="border-t border-gray-200 dark:border-gray-700">
                <td className="px-3 py-2 text-[11px] uppercase tracking-wide text-gray-500 font-semibold" colSpan={3}>
                  Total paid{totalPairs.length > 1 ? ` (${cur})` : ''}
                </td>
                <td className="px-3 py-2 text-right font-bold tabular-nums">{money(sum, cur)}</td>
                <td colSpan={3} />
              </tr>
            ))}
          </tfoot>
        </table>
      </div>
    </div>
  );
}
